// components/Contact.jsx — Premium CTA + Contact Section
import { useState, useCallback } from "react";
import { motion } from "framer-motion";
import emailjs from "@emailjs/browser";

// Inline SVG Icons
const SendIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M22 2L11 13M22 2l-7 20-4-9-9-4 20-7z"
    />
  </svg>
);

const CheckIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M22 11.08V12a10 10 0 11-5.93-9.14"
    />
    <polyline
      strokeLinecap="round"
      strokeLinejoin="round"
      points="22 4 12 14.01 9 11.01"
    />
  </svg>
);

const ArrowRightIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <line x1="5" y1="12" x2="19" y2="12" />
    <polyline points="12 5 19 12 12 19" />
  </svg>
);

const SparklesIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <path
      strokeLinecap="round"
      strokeLinejoin="round"
      d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z"
    />
  </svg>
);

const ClockIcon = ({ className }) => (
  <svg
    className={className}
    fill="none"
    stroke="currentColor"
    strokeWidth={2}
    viewBox="0 0 24 24"
  >
    <circle cx="12" cy="12" r="10" />
    <polyline strokeLinecap="round" strokeLinejoin="round" points="12 6 12 12 16 14" />
  </svg>
);

const LinkedInIcon = ({ className }) => (
  <svg className={className} fill="currentColor" viewBox="0 0 24 24">
    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z" />
  </svg>
);

export default function Contact() {
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
  });
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");
  const [focusedField, setFocusedField] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    const templateParams = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      subject: form.subject,
      message: form.message,
      to_name: "Adsurg Team",
    };

    const backendData = {
      name: form.name,
      email: form.email,
      phone: form.phone,
      subject: form.subject,
      message: form.message,
      type: "CONTACT",
    };

    try {
      try {
        const fetchRes = await fetch(
          process.env.REACT_APP_API_URL
            ? `${process.env.REACT_APP_API_URL}/api/contacts`
            : "http://localhost:5000/api/contacts",
          {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
            },
            body: JSON.stringify(backendData),
          }
        );

        if (fetchRes.ok) {
          console.log("✅ Backend Save SUCCESS");
        } else {
          console.error("❌ Backend Save Error:", await fetchRes.text());
        }
      } catch (backendErr) {
        console.error("❌ Backend Request Error:", backendErr);
      }

      try {
        const response = await emailjs.send(
          "service_1hmotcr",
          "template_t4zvfjk",
          templateParams,
          {
            publicKey: "VWAvmxQ2rX99OLBp",
          }
        );
        console.log("✅ EmailJS SUCCESS!", response.status, response.text);
      } catch (emailErr) {
        console.error("❌ EmailJS ERROR (Ignoring to allow submission):", emailErr);
      }

      setSent(true);
      setForm({ name: "", email: "", phone: "", subject: "", message: "" });
      setTimeout(() => setSent(false), 5000);
    } catch (err) {
      console.error("❌ Global Submission Error:", err);
      setError("Failed to send message. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const contactCards = [
    {
      icon: ClockIcon,
      label: "Working Hours",
      value: "Mon-Sat: 9AM - 7:30PM",
      color: "from-[#00809D] to-[#006d87]",
    },
    {
      icon: LinkedInIcon,
      label: "LinkedIn",
      value: "Adsurg",
      href: "",
      color: "from-[#0077b5] to-[#00a0c4]",
    },
  ];

  const InputField = useCallback(
    ({ id, type = "text", label, required = false, value, onChange }) => (
      <div className="relative group">
        <input
          type={type}
          id={id}
          value={value}
          onChange={onChange}
          required={required}
          onFocus={() => setFocusedField(id)}
          onBlur={() => setFocusedField(null)}
          placeholder=" "
          className="peer w-full bg-white border-2 border-gray-200 rounded-xl px-4 pt-6 pb-2 text-gray-900 text-base focus:border-[#00809D] focus:outline-none transition-all duration-300 hover:border-gray-300"
        />
        <label
          htmlFor={id}
          className="absolute left-4 top-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400 transition-all duration-300 peer-placeholder-shown:top-4 peer-placeholder-shown:text-sm peer-placeholder-shown:font-normal peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal peer-focus:top-2 peer-focus:text-[11px] peer-focus:font-semibold peer-focus:uppercase peer-focus:tracking-wider peer-focus:text-[#00809D]"
        >
          {label}
        </label>
        <div className="absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-[#00809D] rounded-full transition-all duration-500 w-0 peer-focus:w-[calc(100%-2rem)]" />
      </div>
    ),
    []
  );

  return (
    <section id="contact" className="relative bg-white overflow-hidden">
      {/* Top CTA Banner */}
      <div className="relative bg-gradient-to-br from-[#00809D] via-[#007a95] to-[#006d87] py-16 sm:py-20 lg:py-24 overflow-hidden">
        {/* Decorative elements */}
        <div className="absolute inset-0">
          <div className="absolute top-0 right-0 w-96 h-96 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/3" />
          <div className="absolute bottom-0 left-0 w-80 h-80 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/3" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] bg-white/3 rounded-full blur-3xl" />
          <div
            className="absolute inset-0 opacity-[0.03]"
            style={{
              backgroundImage:
                "linear-gradient(rgba(255,255,255,.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,.5) 1px, transparent 1px)",
              backgroundSize: "40px 40px",
            }}
          />
        </div>

        <div className="max-w-4xl mx-auto px-6 relative z-10 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="inline-flex items-center gap-2 bg-white/15 backdrop-blur-sm px-5 py-2 rounded-full mb-6 border border-white/20">
              <SparklesIcon className="w-4 h-4 text-white" />
              <span className="text-white/90 text-sm font-semibold">
                Free Growth Consultation
              </span>
            </div>

            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mb-5 leading-tight">
              Ready to Scale Your
              <span className="block mt-1">Advertising Results?</span>
            </h2>
            <p className="text-white/70 text-base sm:text-lg max-w-xl mx-auto mb-8 leading-relaxed">
              Join 150+ businesses that have transformed their digital advertising
              with our data-driven strategies.
            </p>
          </motion.div>

          {/* Contact Info Cards */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-wrap justify-center gap-4 sm:gap-5"
          >
            {contactCards.map((card, i) => (
              <div
                key={i}
                className="flex items-center gap-3 bg-white/10 backdrop-blur-sm px-5 py-3.5 rounded-2xl border border-white/15 hover:bg-white/15 transition-all duration-300 group"
              >
                <div
                  className={`w-10 h-10 rounded-xl bg-gradient-to-br ${card.color} flex items-center justify-center shadow-lg`}
                >
                  <card.icon className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <p className="text-white/50 text-xs font-medium">{card.label}</p>
                  {card.href ? (
                    <a
                      href={card.href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-white font-semibold text-sm hover:underline"
                    >
                      {card.value}
                    </a>
                  ) : (
                    <p className="text-white font-semibold text-sm">{card.value}</p>
                  )}
                </div>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Form Section */}
      <div className="relative py-16 sm:py-20 lg:py-24 bg-gray-50">
        <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#00809D]/20 to-transparent" />

        <div className="max-w-5xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-5 gap-10 lg:gap-14 items-start">
            {/* Left side info */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
              className="lg:col-span-2 space-y-8"
            >
              <div>
                <h3 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-3">
                  Let's Talk <span className="text-[#00809D]">Growth</span>
                </h3>
                <p className="text-gray-500 leading-relaxed">
                  Tell us about your business and goals. We'll create a customized
                  advertising strategy that delivers real results.
                </p>
              </div>

              {/* Benefits */}
              <div className="space-y-4">
                {[
                  "Free campaign audit & strategy session",
                  "Custom plan tailored to your goals",
                  "No long-term contracts required",
                  "Dedicated account manager",
                ].map((item, i) => (
                  <motion.div
                    key={i}
                    initial={{ opacity: 0, x: -10 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: 0.1 * i }}
                    className="flex items-center gap-3"
                  >
                    <div className="w-6 h-6 rounded-full bg-[#00809D]/10 flex items-center justify-center flex-shrink-0">
                      <CheckIcon className="w-3.5 h-3.5 text-[#00809D]" />
                    </div>
                    <span className="text-gray-600 text-sm">{item}</span>
                  </motion.div>
                ))}
              </div>

              {/* Response time */}
              <div className="flex items-center gap-3 bg-[#00809D]/5 border border-[#00809D]/10 px-4 py-3 rounded-xl">
                <ClockIcon className="w-5 h-5 text-[#00809D]" />
                <div>
                  <p className="text-sm font-semibold text-gray-800">Quick Response</p>
                  <p className="text-xs text-gray-500">
                    We typically reply within 2-4 hours
                  </p>
                </div>
              </div>

              {/* Trust badges */}
              <div className="flex items-center gap-4 pt-2">
                <div className="flex -space-x-2">
                  {["#00809D", "#E4405F", "#4285F4", "#FF9900"].map((color, i) => (
                    <div
                      key={i}
                      className="w-8 h-8 rounded-full border-2 border-white flex items-center justify-center text-white text-xs font-bold shadow-sm"
                      style={{ backgroundColor: color }}
                    >
                      {["A", "S", "M", "K"][i]}
                    </div>
                  ))}
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-800">
                    150+ Happy Clients
                  </p>
                  <div className="flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <svg
                        key={i}
                        className="w-3 h-3 text-yellow-400 fill-current"
                        viewBox="0 0 20 20"
                      >
                        <path d="M10 15l-5.878 3.09 1.123-6.545L.489 6.91l6.572-.955L10 0l2.939 5.955 6.572.955-4.756 4.635 1.123 6.545z" />
                      </svg>
                    ))}
                    <span className="text-xs text-gray-400 ml-1">4.9/5</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Right side form */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.1 }}
              className="lg:col-span-3"
            >
              <div className="bg-white rounded-2xl border border-gray-200 p-6 sm:p-8 shadow-lg shadow-gray-200/50">
                {/* Success message */}
                {sent && (
                  <motion.div
                    initial={{ opacity: 0, scale: 0.95 }}
                    animate={{ opacity: 1, scale: 1 }}
                    className="mb-6 p-4 bg-green-50 border border-green-200 rounded-xl flex items-center gap-3"
                  >
                    <CheckIcon className="w-5 h-5 text-green-600 flex-shrink-0" />
                    <div>
                      <p className="text-green-800 font-semibold text-sm">
                        Enquiry Sent Successfully!
                      </p>
                      <p className="text-green-600 text-xs">
                        We'll get back to you within 2-4 hours.
                      </p>
                    </div>
                  </motion.div>
                )}

                {error && (
                  <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-xl text-red-600 text-sm">
                    {error}
                  </div>
                )}

                <form onSubmit={handleSubmit} className="space-y-5">
                  <div className="grid sm:grid-cols-2 gap-5">
                    <InputField
                      id="name"
                      label="Full Name *"
                      required
                      value={form.name}
                      onChange={(e) => setForm({ ...form, name: e.target.value })}
                    />
                    <InputField
                      id="phone"
                      type="tel"
                      label="Phone Number"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    />
                  </div>

                  <div className="grid sm:grid-cols-2 gap-5">
                    <InputField
                      id="email"
                      type="email"
                      label="Email Address *"
                      required
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />

                    <div className="relative group">
                      <select
                        id="subject"
                        value={form.subject}
                        onChange={(e) => setForm({ ...form, subject: e.target.value })}
                        required
                        onFocus={() => setFocusedField("subject")}
                        onBlur={() => setFocusedField(null)}
                        className="peer w-full bg-white border-2 border-gray-200 rounded-xl px-4 pt-6 pb-2 text-gray-900 text-base focus:border-[#00809D] focus:outline-none transition-all duration-300 hover:border-gray-300 appearance-none cursor-pointer"
                      >
                        <option value="" disabled hidden></option>
                        <option value="General Inquiry">General Inquiry</option>
                        <option value="Campaign Management">Campaign Management</option>
                        <option value="Pricing & Plans">Pricing & Plans</option>
                        <option value="Technical Support">Technical Support</option>
                        <option value="Partnership">Partnership</option>
                      </select>
                      <label
                        htmlFor="subject"
                        className="absolute left-4 top-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400 transition-all duration-300 peer-placeholder-shown:top-4 peer-placeholder-shown:text-sm peer-placeholder-shown:font-normal peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal peer-focus:top-2 peer-focus:text-[11px] peer-focus:font-semibold peer-focus:uppercase peer-focus:tracking-wider peer-focus:text-[#00809D]"
                      >
                        Subject *
                      </label>
                      <div
                        className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-[#00809D] rounded-full transition-all duration-500 ${
                          focusedField === "subject"
                            ? "w-[calc(100%-2rem)]"
                            : "w-0"
                        }`}
                      />
                      <div className="absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none">
                        <svg
                          className="w-4 h-4 text-gray-400"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>

                  <div className="relative group">
                    <textarea
                      id="message"
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      required
                      rows="4"
                      onFocus={() => setFocusedField("message")}
                      onBlur={() => setFocusedField(null)}
                      placeholder=" "
                      className="peer w-full bg-white border-2 border-gray-200 rounded-xl px-4 pt-6 pb-2 text-gray-900 text-base focus:border-[#00809D] focus:outline-none transition-all duration-300 resize-none hover:border-gray-300"
                    />
                    <label
                      htmlFor="message"
                      className="absolute left-4 top-2 text-[11px] font-semibold uppercase tracking-wider text-gray-400 transition-all duration-300 peer-placeholder-shown:top-4 peer-placeholder-shown:text-sm peer-placeholder-shown:font-normal peer-placeholder-shown:normal-case peer-placeholder-shown:tracking-normal peer-focus:top-2 peer-focus:text-[11px] peer-focus:font-semibold peer-focus:uppercase peer-focus:tracking-wider peer-focus:text-[#00809D]"
                    >
                      Tell us about your project *
                    </label>
                    <div
                      className={`absolute bottom-0 left-1/2 -translate-x-1/2 h-0.5 bg-[#00809D] rounded-full transition-all duration-500 ${
                        focusedField === "message"
                          ? "w-[calc(100%-2rem)]"
                          : "w-0"
                      }`}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={loading}
                    className={`w-full py-4 rounded-xl font-bold text-base transition-all duration-300 flex items-center justify-center gap-2.5 shadow-lg hover:shadow-xl active:scale-[0.98] disabled:opacity-70 disabled:cursor-not-allowed ${
                      sent
                        ? "bg-green-500 hover:bg-green-600 text-white shadow-green-500/25"
                        : "bg-[#00809D] hover:bg-[#006d87] text-white shadow-[#00809D]/25 hover:shadow-[#00809D]/40"
                    }`}
                  >
                    {loading ? (
                      <>
                        <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                        Sending...
                      </>
                    ) : sent ? (
                      <>
                        <CheckIcon className="w-5 h-5" />
                        Enquiry Sent Successfully
                      </>
                    ) : (
                      <>
                        <SendIcon className="w-5 h-5" />
                        Submit Your Enquiry
                        <ArrowRightIcon className="w-4 h-4 ml-1" />
                      </>
                    )}
                  </button>

                  <p className="text-center text-xs text-gray-400 mt-3 flex items-center justify-center gap-1.5">
                    <svg
                      className="w-3.5 h-3.5"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth={2}
                      viewBox="0 0 24 24"
                    >
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0110 0v4" />
                    </svg>
                    Your information is secure and will never be shared.
                  </p>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </div>
    </section>
  );
}